/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  Modal,
  StatusBar,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Alert,
  Image,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome,
  AntDesign,
  MaterialIcons,
} from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

// ==================== ✅ API CONFIGURATION ====================
const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  projects: `${API_BASE_URL}/api/projects`,
  projectsData: `${API_BASE_URL}/api/data/projects`, // fallback
  companyProfile: `${API_BASE_URL}/api/data/company_profiles`,
};

// ✅ Axios instance with API key and auth interceptor
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
  },
});

api.interceptors.request.use(
  async (config) => {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        config.params = { ...config.params, company_id: parseInt(companyId) };
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Custom date picker component (unchanged)
const CustomDatePicker = ({
  isVisible,
  onClose,
  onSelectDate,
  initialDate,
}) => {
  const [selectedDate, setSelectedDate] = useState(initialDate || new Date());
  const [selectedYear, setSelectedYear] = useState(
    initialDate ? initialDate.getFullYear() : new Date().getFullYear()
  );
  const [selectedMonth, setSelectedMonth] = useState(
    initialDate ? initialDate.getMonth() : new Date().getMonth()
  );
  const [selectedDay, setSelectedDay] = useState(
    initialDate ? initialDate.getDate() : new Date().getDate()
  );

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 16 }, (_, i) => currentYear - 5 + i);

  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

  const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  useEffect(() => {
    const newDate = new Date(selectedYear, selectedMonth, selectedDay);
    setSelectedDate(newDate);
  }, [selectedYear, selectedMonth, selectedDay]);

  const handleConfirm = () => {
    onSelectDate(selectedDate);
    onClose();
  };

  if (!isVisible) return null;

  return (
    <Modal animationType="slide" transparent={true} visible={isVisible} onRequestClose={onClose}>
      <View style={customDatePickerStyles.modalOverlay}>
        <View style={customDatePickerStyles.modalContent}>
          <View style={customDatePickerStyles.header}>
            <Text style={customDatePickerStyles.title}>Select Date</Text>
            <TouchableOpacity onPress={onClose} style={customDatePickerStyles.closeButton}>
              <Ionicons name="close" size={24} color="#64748B" />
            </TouchableOpacity>
          </View>

          <View style={customDatePickerStyles.dateDisplay}>
            <Text style={customDatePickerStyles.dateText}>
              {selectedDate.toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </Text>
          </View>

          <View style={customDatePickerStyles.pickerContainer}>
            {/* Month Picker */}
            <View style={customDatePickerStyles.pickerColumn}>
              <Text style={customDatePickerStyles.pickerLabel}>Month</Text>
              <ScrollView style={customDatePickerStyles.picker} showsVerticalScrollIndicator={false}>
                {months.map((month, index) => (
                  <TouchableOpacity
                    key={month}
                    style={[
                      customDatePickerStyles.pickerItem,
                      selectedMonth === index && customDatePickerStyles.pickerItemSelected,
                    ]}
                    onPress={() => {
                      setSelectedMonth(index);
                      const maxDays = getDaysInMonth(selectedYear, index);
                      if (selectedDay > maxDays) setSelectedDay(maxDays);
                    }}
                  >
                    <Text
                      style={[
                        customDatePickerStyles.pickerItemText,
                        selectedMonth === index && customDatePickerStyles.pickerItemTextSelected,
                      ]}
                    >
                      {month}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>

            {/* Day Picker */}
            <View style={customDatePickerStyles.pickerColumn}>
              <Text style={customDatePickerStyles.pickerLabel}>Day</Text>
              <ScrollView style={customDatePickerStyles.picker} showsVerticalScrollIndicator={false}>
                {days.map(day => (
                  <TouchableOpacity
                    key={day}
                    style={[
                      customDatePickerStyles.pickerItem,
                      selectedDay === day && customDatePickerStyles.pickerItemSelected,
                    ]}
                    onPress={() => setSelectedDay(day)}
                  >
                    <Text
                      style={[
                        customDatePickerStyles.pickerItemText,
                        selectedDay === day && customDatePickerStyles.pickerItemTextSelected,
                      ]}
                    >
                      {day}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>

            {/* Year Picker */}
            <View style={customDatePickerStyles.pickerColumn}>
              <Text style={customDatePickerStyles.pickerLabel}>Year</Text>
              <ScrollView style={customDatePickerStyles.picker} showsVerticalScrollIndicator={false}>
                {years.map(year => (
                  <TouchableOpacity
                    key={year}
                    style={[
                      customDatePickerStyles.pickerItem,
                      selectedYear === year && customDatePickerStyles.pickerItemSelected,
                    ]}
                    onPress={() => {
                      setSelectedYear(year);
                      const maxDays = getDaysInMonth(year, selectedMonth);
                      if (selectedDay > maxDays) setSelectedDay(maxDays);
                    }}
                  >
                    <Text
                      style={[
                        customDatePickerStyles.pickerItemText,
                        selectedYear === year && customDatePickerStyles.pickerItemTextSelected,
                      ]}
                    >
                      {year}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          </View>

          <TouchableOpacity style={customDatePickerStyles.confirmButton} onPress={handleConfirm}>
            <Text style={customDatePickerStyles.confirmButtonText}>Confirm</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

const customDatePickerStyles = StyleSheet.create({
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' },
  modalContent: { backgroundColor: '#fff', borderRadius: 12, width: '90%', padding: 20, maxHeight: '80%' },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  title: { fontSize: 18, fontWeight: '600', color: '#0f172a' },
  closeButton: { padding: 4 },
  dateDisplay: { alignItems: 'center', marginBottom: 20, paddingVertical: 10, backgroundColor: '#f1f5f9', borderRadius: 8 },
  dateText: { fontSize: 18, fontWeight: '500', color: '#0e4e96' },
  pickerContainer: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 20 },
  pickerColumn: { flex: 1, marginHorizontal: 4 },
  pickerLabel: { fontSize: 14, fontWeight: '500', color: '#64748B', marginBottom: 8, textAlign: 'center' },
  picker: { height: 150, backgroundColor: '#f8fafc', borderRadius: 8 },
  pickerItem: { paddingVertical: 10, alignItems: 'center', justifyContent: 'center' },
  pickerItemSelected: { backgroundColor: '#e0f2fe' },
  pickerItemText: { fontSize: 16, color: '#334155' },
  pickerItemTextSelected: { color: '#0e4e96', fontWeight: '600' },
  confirmButton: { backgroundColor: '#0e4e96', borderRadius: 8, paddingVertical: 12, alignItems: 'center', justifyContent: 'center' },
  confirmButtonText: { color: '#fff', fontSize: 16, fontWeight: '600' },
});

export default function AddProjectScreen() {
  const navigation = useNavigation();
  const [loading, setLoading] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [currentDateField, setCurrentDateField] = useState(null);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showMilestoneModal, setShowMilestoneModal] = useState(false);
  const [showRiskModal, setShowRiskModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [teamMember, setTeamMember] = useState({
    name: '',
    role: '',
    avatar: 'https://randomuser.me/api/portraits/men/1.jpg',
  });
  const [milestone, setMilestone] = useState({
    title: '',
    dueDate: new Date(),
    status: 'Not Started',
  });
  const [risk, setRisk] = useState({
    description: '',
    severity: 'Medium',
    mitigation: '',
  });
  const [tag, setTag] = useState('');
  const [projectData, setProjectData] = useState({
    title: '',
    description: '',
    client: '',
    location: '',
    startDate: new Date(),
    endDate: new Date(new Date().setMonth(new Date().getMonth() + 6)),
    budget: '',
    status: 'Planning',
    priority: 'Medium',
    team: [],
    milestones: [],
    risks: [],
    tags: [],
  });

  // Load company logo and ID
  useEffect(() => {
    loadCompanyData();
  }, []);

  const loadCompanyData = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        setCompanyLogo(profile.logo || null);
      }
      const storedCompanyId = await AsyncStorage.getItem('company_id');
      if (storedCompanyId) {
        setCompanyId(parseInt(storedCompanyId));
      }

      const companyIdFromStorage = await AsyncStorage.getItem('company_id');
      if (companyIdFromStorage) {
        const response = await api.get(API_ENDPOINTS.companyProfile, {
          params: { company_id: parseInt(companyIdFromStorage) },
        });
        if (response.data?.success && response.data.data && response.data.data.length > 0) {
          const latestProfile = response.data.data[0];
          setCompanyLogo(latestProfile.logo || null);
          await AsyncStorage.setItem('company_profile', JSON.stringify(latestProfile));
        }
      }
    } catch (error) {
      console.warn('Error loading company data:', error);
    }
  };

  // Status options
  const statusOptions = ['Planning', 'Active', 'On Hold', 'Completed'];
  const priorityOptions = ['Low', 'Medium', 'High'];
  const severityOptions = ['Low', 'Medium', 'High'];
  const milestoneStatusOptions = ['Not Started', 'In Progress', 'Completed', 'On Hold'];

  const handleGoBack = () => navigation.goBack();

  const showDatePickerModal = (field) => {
    setCurrentDateField(field);
    setShowDatePicker(true);
  };

  const handleDateSelection = (selectedDate) => {
    setProjectData({ ...projectData, [currentDateField]: selectedDate });
    setShowDatePicker(false);
  };

  const handleMilestoneDateSelection = (selectedDate) => {
    setMilestone({ ...milestone, dueDate: selectedDate });
  };

  const handleAddTeamMember = () => {
    if (teamMember.name.trim() === '' || teamMember.role.trim() === '') {
      Alert.alert('Error', 'Please enter both name and role for the team member');
      return;
    }
    const newTeamMember = { ...teamMember, id: `t${projectData.team.length + 1}` };
    setProjectData({ ...projectData, team: [...projectData.team, newTeamMember] });
    setTeamMember({ name: '', role: '', avatar: 'https://randomuser.me/api/portraits/men/1.jpg' });
    setShowTeamModal(false);
  };

  const handleAddMilestone = () => {
    if (milestone.title.trim() === '') {
      Alert.alert('Error', 'Please enter a title for the milestone');
      return;
    }
    const newMilestone = { ...milestone, id: `m${projectData.milestones.length + 1}` };
    setProjectData({ ...projectData, milestones: [...projectData.milestones, newMilestone] });
    setMilestone({ title: '', dueDate: new Date(), status: 'Not Started' });
    setShowMilestoneModal(false);
  };

  const handleAddRisk = () => {
    if (risk.description.trim() === '' || risk.mitigation.trim() === '') {
      Alert.alert('Error', 'Please enter both description and mitigation for the risk');
      return;
    }
    const newRisk = { ...risk, id: `r${projectData.risks.length + 1}` };
    setProjectData({ ...projectData, risks: [...projectData.risks, newRisk] });
    setRisk({ description: '', severity: 'Medium', mitigation: '' });
    setShowRiskModal(false);
  };

  const handleAddTag = () => {
    if (tag.trim() === '') {
      Alert.alert('Error', 'Please enter a tag');
      return;
    }
    if (projectData.tags.includes(tag.trim())) {
      Alert.alert('Error', 'This tag already exists');
      return;
    }
    setProjectData({ ...projectData, tags: [...projectData.tags, tag.trim()] });
    setTag('');
    setShowTagModal(false);
  };

  const handleRemoveTeamMember = (id) => {
    setProjectData({ ...projectData, team: projectData.team.filter(member => member.id !== id) });
  };

  const handleRemoveMilestone = (id) => {
    setProjectData({ ...projectData, milestones: projectData.milestones.filter(item => item.id !== id) });
  };

  const handleRemoveRisk = (id) => {
    setProjectData({ ...projectData, risks: projectData.risks.filter(item => item.id !== id) });
  };

  const handleRemoveTag = (tagToRemove) => {
    setProjectData({ ...projectData, tags: projectData.tags.filter(item => item !== tagToRemove) });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const handleSaveProject = async () => {
    if (
      projectData.title.trim() === '' ||
      projectData.client.trim() === '' ||
      projectData.location.trim() === '' ||
      projectData.budget.trim() === '' ||
      projectData.description.trim() === ''
    ) {
      Alert.alert('Error', 'Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      // Prepare payload for API
      const payload = {
        name: projectData.title,
        description: projectData.description,
        client_name: projectData.client,
        location: projectData.location,
        start_date: projectData.startDate.toISOString(),
        deadline: projectData.endDate.toISOString(),
        budget: parseFloat(projectData.budget),
        status: String(projectData.status ?? '').toLowerCase(),
        priority: String(projectData.priority ?? '').toLowerCase(),
      };

      // Add company_id if available (some endpoints expect it in the body)
      if (companyId) {
        payload.company_id = companyId;
      }

      console.log('Sending payload:', payload);

      // Try primary endpoint first
      let response;
      try {
        response = await api.post(API_ENDPOINTS.projects, payload);
        console.log('Primary endpoint response:', response.data);
      } catch (primaryError) {
        console.warn('Primary endpoint failed, trying fallback:', primaryError.message);
        // If primary fails, try the /api/data/projects endpoint
        response = await api.post(API_ENDPOINTS.projectsData, payload);
        console.log('Fallback endpoint response:', response.data);
      }

      // Check for success in various response formats
      if (
        response.status === 201 ||
        response.status === 200 ||
        response.data?.success === true ||
        response.data?.id // sometimes returns the created object directly
      ) {
        Alert.alert('Success', 'Project has been created successfully', [
          { text: 'OK', onPress: () => navigation.goBack() },
        ]);
      } else {
        throw new Error(response.data?.message || 'Unexpected server response');
      }
    } catch (error) {
      console.error('Error creating project:', error);
      // Show detailed error message if available
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to create project. Please try again.';
      Alert.alert('Error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return '#10B981';
      case 'Planning': return '#8B5CF6';
      case 'On Hold': return '#F59E0B';
      case 'Completed': return '#0e4e96';
      default: return '#64748B';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return '#EF4444';
      case 'Medium': return '#F59E0B';
      case 'Low': return '#10B981';
      default: return '#64748B';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'High': return '#EF4444';
      case 'Medium': return '#F59E0B';
      case 'Low': return '#10B981';
      default: return '#64748B';
    }
  };

  const getMilestoneStatusColor = (status) => {
    switch (status) {
      case 'Completed': return '#10B981';
      case 'In Progress': return '#F59E0B';
      case 'On Hold': return '#8B5CF6';
      case 'Not Started': return '#64748B';
      default: return '#64748B';
    }
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      {/* Header with Back Button, Company Logo, and Title */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
          <Ionicons name="chevron-back" size={24} color="#fff" />
        </TouchableOpacity>

        <View style={styles.headerCenter}>
          {companyLogo ? (
            <Image source={{ uri: companyLogo || undefined }} style={styles.headerLogo} />
          ) : (
            <MaterialCommunityIcons name="office-building" size={30} color="#fff" />
          )}
          <Text style={styles.headerTitle}>Add New Project</Text>
        </View>

        <View style={{ width: 24 }} />
      </View>

      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={{ flex: 1 }}>
        <ScrollView contentContainerStyle={styles.container} showsVerticalScrollIndicator={false}>
          {/* Basic Information Section */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Basic Information</Text>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Project Title *</Text>
              <TextInput
                style={styles.input}
                placeholder="Enter project title"
                placeholderTextColor="#94A3B8"
                value={projectData.title}
                onChangeText={text => setProjectData({ ...projectData, title: text })}
              />
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Client *</Text>
              <TextInput
                style={styles.input}
                placeholder="Enter client name"
                placeholderTextColor="#94A3B8"
                value={projectData.client}
                onChangeText={text => setProjectData({ ...projectData, client: text })}
              />
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Location *</Text>
              <TextInput
                style={styles.input}
                placeholder="Enter project location"
                placeholderTextColor="#94A3B8"
                value={projectData.location}
                onChangeText={text => setProjectData({ ...projectData, location: text })}
              />
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Budget *</Text>
              <TextInput
                style={styles.input}
                placeholder="Enter project budget"
                placeholderTextColor="#94A3B8"
                value={projectData.budget}
                onChangeText={text => setProjectData({ ...projectData, budget: text })}
                keyboardType="decimal-pad"
              />
            </View>
            <View style={styles.rowInputs}>
              <View style={[styles.inputGroup, { flex: 1, marginRight: 8 }]}>
                <Text style={styles.inputLabel}>Start Date *</Text>
                <TouchableOpacity
                  style={styles.dateInput}
                  onPress={() => showDatePickerModal('startDate')}
                >
                  <Text style={styles.dateText}>{formatDate(projectData.startDate)}</Text>
                  <Ionicons name="calendar-outline" size={20} color="#64748B" />
                </TouchableOpacity>
              </View>
              <View style={[styles.inputGroup, { flex: 1, marginLeft: 8 }]}>
                <Text style={styles.inputLabel}>End Date *</Text>
                <TouchableOpacity
                  style={styles.dateInput}
                  onPress={() => showDatePickerModal('endDate')}
                >
                  <Text style={styles.dateText}>{formatDate(projectData.endDate)}</Text>
                  <Ionicons name="calendar-outline" size={20} color="#64748B" />
                </TouchableOpacity>
              </View>
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Description *</Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                placeholder="Enter project description"
                placeholderTextColor="#94A3B8"
                value={projectData.description}
                onChangeText={text => setProjectData({ ...projectData, description: text })}
                multiline
                numberOfLines={4}
                textAlignVertical="top"
              />
            </View>
          </View>

          {/* Status and Priority Section */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Status & Priority</Text>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Project Status</Text>
              <View style={styles.optionsContainer}>
                {statusOptions.map(status => (
                  <TouchableOpacity
                    key={status}
                    style={[
                      styles.optionButton,
                      projectData.status === status && styles.optionButtonActive,
                      projectData.status === status && { borderColor: getStatusColor(status) },
                    ]}
                    onPress={() => setProjectData({ ...projectData, status })}
                  >
                    <View style={[styles.statusDot, { backgroundColor: getStatusColor(status) }]} />
                    <Text
                      style={[
                        styles.optionButtonText,
                        projectData.status === status && styles.optionButtonTextActive,
                        projectData.status === status && { color: getStatusColor(status) },
                      ]}
                    >
                      {status}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Priority Level</Text>
              <View style={styles.optionsContainer}>
                {priorityOptions.map(priority => (
                  <TouchableOpacity
                    key={priority}
                    style={[
                      styles.optionButton,
                      projectData.priority === priority && styles.optionButtonActive,
                      projectData.priority === priority && { borderColor: getPriorityColor(priority) },
                    ]}
                    onPress={() => setProjectData({ ...projectData, priority })}
                  >
                    <View style={[styles.priorityDot, { backgroundColor: getPriorityColor(priority) }]} />
                    <Text
                      style={[
                        styles.optionButtonText,
                        projectData.priority === priority && styles.optionButtonTextActive,
                        projectData.priority === priority && { color: getPriorityColor(priority) },
                      ]}
                    >
                      {priority}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </View>

          {/* Team Section */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Project Team</Text>
              <TouchableOpacity style={styles.addButton} onPress={() => setShowTeamModal(true)}>
                <Ionicons name="add" size={20} color="#fff" />
                <Text style={styles.addButtonText}>Add Member</Text>
              </TouchableOpacity>
            </View>
            {projectData.team.length === 0 ? (
              <View style={styles.emptyState}>
                <MaterialCommunityIcons name="account-group-outline" size={40} color="#CBD5E1" />
                <Text style={styles.emptyStateText}>No team members added yet</Text>
              </View>
            ) : (
              <View style={styles.teamList}>
                {projectData.team.map(member => (
                  <View key={member.id} style={styles.teamMember}>
                    <View style={styles.teamMemberInfo}>
                      <Text style={styles.teamMemberName}>{member.name}</Text>
                      <Text style={styles.teamMemberRole}>{member.role}</Text>
                    </View>
                    <TouchableOpacity style={styles.removeButton} onPress={() => handleRemoveTeamMember(member.id)}>
                      <Ionicons name="close-circle" size={20} color="#EF4444" />
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
            )}
          </View>

          {/* Milestones Section */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Project Milestones</Text>
              <TouchableOpacity style={styles.addButton} onPress={() => setShowMilestoneModal(true)}>
                <Ionicons name="add" size={20} color="#fff" />
                <Text style={styles.addButtonText}>Add Milestone</Text>
              </TouchableOpacity>
            </View>
            {projectData.milestones.length === 0 ? (
              <View style={styles.emptyState}>
                <MaterialCommunityIcons name="flag-outline" size={40} color="#CBD5E1" />
                <Text style={styles.emptyStateText}>No milestones added yet</Text>
              </View>
            ) : (
              <View style={styles.milestonesList}>
                {projectData.milestones.map(item => (
                  <View key={item.id} style={styles.milestoneItem}>
                    <View style={styles.milestoneInfo}>
                      <Text style={styles.milestoneTitle}>{item.title}</Text>
                      <View style={styles.milestoneDetails}>
                        <View style={styles.milestoneDetail}>
                          <Ionicons name="calendar-outline" size={16} color="#64748B" />
                          <Text style={styles.milestoneDetailText}>{formatDate(item.dueDate)}</Text>
                        </View>
                        <View style={styles.milestoneDetail}>
                          <MaterialCommunityIcons
                            name={item.status === 'Completed' ? 'check-circle' : item.status === 'In Progress' ? 'progress-clock' : item.status === 'On Hold' ? 'pause-circle' : 'circle-outline'}
                            size={16}
                            color={getMilestoneStatusColor(item.status)}
                          />
                          <Text style={[styles.milestoneDetailText, { color: getMilestoneStatusColor(item.status) }]}>
                            {item.status}
                          </Text>
                        </View>
                      </View>
                    </View>
                    <TouchableOpacity style={styles.removeButton} onPress={() => handleRemoveMilestone(item.id)}>
                      <Ionicons name="close-circle" size={20} color="#EF4444" />
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
            )}
          </View>

          {/* Risks Section */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Risk Assessment</Text>
              <TouchableOpacity style={styles.addButton} onPress={() => setShowRiskModal(true)}>
                <Ionicons name="add" size={20} color="#fff" />
                <Text style={styles.addButtonText}>Add Risk</Text>
              </TouchableOpacity>
            </View>
            {projectData.risks.length === 0 ? (
              <View style={styles.emptyState}>
                <MaterialCommunityIcons name="alert-outline" size={40} color="#CBD5E1" />
                <Text style={styles.emptyStateText}>No risks added yet</Text>
              </View>
            ) : (
              <View style={styles.risksList}>
                {projectData.risks.map(item => (
                  <View key={item.id} style={styles.riskItem}>
                    <View style={styles.riskInfo}>
                      <View style={styles.riskHeader}>
                        <View style={[styles.severityBadge, { backgroundColor: item.severity === 'High' ? '#FEE2E2' : item.severity === 'Medium' ? '#FEF3C7' : '#DCFCE7' }]}>
                          <Text style={[styles.severityText, { color: item.severity === 'High' ? '#B91C1C' : item.severity === 'Medium' ? '#B45309' : '#166534' }]}>
                            {item.severity}
                          </Text>
                        </View>
                      </View>
                      <Text style={styles.riskDescription}>{item.description}</Text>
                      <View style={styles.riskMitigation}>
                        <Text style={styles.riskMitigationLabel}>Mitigation:</Text>
                        <Text style={styles.riskMitigationText}>{item.mitigation}</Text>
                      </View>
                    </View>
                    <TouchableOpacity style={styles.removeButton} onPress={() => handleRemoveRisk(item.id)}>
                      <Ionicons name="close-circle" size={20} color="#EF4444" />
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
            )}
          </View>

          {/* Tags Section */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Project Tags</Text>
              <TouchableOpacity style={styles.addButton} onPress={() => setShowTagModal(true)}>
                <Ionicons name="add" size={20} color="#fff" />
                <Text style={styles.addButtonText}>Add Tag</Text>
              </TouchableOpacity>
            </View>
            {projectData.tags.length === 0 ? (
              <View style={styles.emptyState}>
                <MaterialCommunityIcons name="tag-outline" size={40} color="#CBD5E1" />
                <Text style={styles.emptyStateText}>No tags added yet</Text>
              </View>
            ) : (
              <View style={styles.tagsContainer}>
                {projectData.tags.map((item, index) => (
                  <View key={index} style={styles.tag}>
                    <Text style={styles.tagText}>{item}</Text>
                    <TouchableOpacity style={styles.removeTagButton} onPress={() => handleRemoveTag(item)}>
                      <Ionicons name="close-circle" size={16} color="#64748B" />
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
            )}
          </View>

          {/* Save Button */}
          <TouchableOpacity style={styles.saveButton} onPress={handleSaveProject} disabled={loading}>
            {loading ? (
              <ActivityIndicator color="#fff" size={20} />
            ) : (
              <>
                <Ionicons name="save-outline" size={20} color="#fff" />
                <Text style={styles.saveButtonText}>Save Project</Text>
              </>
            )}
          </TouchableOpacity>
        </ScrollView>
      </KeyboardAvoidingView>

      {/* Custom Date Picker Modal */}
      <CustomDatePicker
        isVisible={showDatePicker}
        onClose={() => setShowDatePicker(false)}
        onSelectDate={handleDateSelection}
        initialDate={projectData[currentDateField]}
      />

      {/* Add Team Member Modal */}
      <Modal animationType="slide" transparent={true} visible={showTeamModal} onRequestClose={() => setShowTeamModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Add Team Member</Text>
              <TouchableOpacity style={styles.modalCloseButton} onPress={() => setShowTeamModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Name *</Text>
              <TextInput
                style={styles.input}
                placeholder="Enter team member name"
                placeholderTextColor="#94A3B8"
                value={teamMember.name}
                onChangeText={text => setTeamMember({ ...teamMember, name: text })}
              />
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Role *</Text>
              <TextInput
                style={styles.input}
                placeholder="Enter team member role"
                placeholderTextColor="#94A3B8"
                value={teamMember.role}
                onChangeText={text => setTeamMember({ ...teamMember, role: text })}
              />
            </View>
            <TouchableOpacity style={styles.modalActionButton} onPress={handleAddTeamMember}>
              <Text style={styles.modalActionButtonText}>Add Team Member</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Add Milestone Modal */}
      <Modal animationType="slide" transparent={true} visible={showMilestoneModal} onRequestClose={() => setShowMilestoneModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Add Milestone</Text>
              <TouchableOpacity style={styles.modalCloseButton} onPress={() => setShowMilestoneModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Title *</Text>
              <TextInput
                style={styles.input}
                placeholder="Enter milestone title"
                placeholderTextColor="#94A3B8"
                value={milestone.title}
                onChangeText={text => setMilestone({ ...milestone, title: text })}
              />
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Due Date *</Text>
              <TouchableOpacity
                style={styles.dateInput}
                onPress={() => {
                  setCurrentDateField('milestoneDueDate');
                  setShowDatePicker(true);
                }}
              >
                <Text style={styles.dateText}>{formatDate(milestone.dueDate)}</Text>
                <Ionicons name="calendar-outline" size={20} color="#64748B" />
              </TouchableOpacity>
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Status</Text>
              <View style={styles.optionsContainer}>
                {milestoneStatusOptions.map(status => (
                  <TouchableOpacity
                    key={status}
                    style={[
                      styles.optionButton,
                      milestone.status === status && styles.optionButtonActive,
                      milestone.status === status && { borderColor: getMilestoneStatusColor(status) },
                    ]}
                    onPress={() => setMilestone({ ...milestone, status })}
                  >
                    <MaterialCommunityIcons
                      name={status === 'Completed' ? 'check-circle' : status === 'In Progress' ? 'progress-clock' : status === 'On Hold' ? 'pause-circle' : 'circle-outline'}
                      size={16}
                      color={getMilestoneStatusColor(status)}
                    />
                    <Text
                      style={[
                        styles.optionButtonText,
                        milestone.status === status && styles.optionButtonTextActive,
                        milestone.status === status && { color: getMilestoneStatusColor(status) },
                      ]}
                    >
                      {status}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            <TouchableOpacity style={styles.modalActionButton} onPress={handleAddMilestone}>
              <Text style={styles.modalActionButtonText}>Add Milestone</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Add Risk Modal */}
      <Modal animationType="slide" transparent={true} visible={showRiskModal} onRequestClose={() => setShowRiskModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Add Risk</Text>
              <TouchableOpacity style={styles.modalCloseButton} onPress={() => setShowRiskModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Description *</Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                placeholder="Enter risk description"
                placeholderTextColor="#94A3B8"
                value={risk.description}
                onChangeText={text => setRisk({ ...risk, description: text })}
                multiline
                numberOfLines={3}
                textAlignVertical="top"
              />
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Severity</Text>
              <View style={styles.optionsContainer}>
                {severityOptions.map(severity => (
                  <TouchableOpacity
                    key={severity}
                    style={[
                      styles.optionButton,
                      risk.severity === severity && styles.optionButtonActive,
                      risk.severity === severity && { borderColor: getSeverityColor(severity) },
                    ]}
                    onPress={() => setRisk({ ...risk, severity })}
                  >
                    <View style={[styles.severityDot, { backgroundColor: getSeverityColor(severity) }]} />
                    <Text
                      style={[
                        styles.optionButtonText,
                        risk.severity === severity && styles.optionButtonTextActive,
                        risk.severity === severity && { color: getSeverityColor(severity) },
                      ]}
                    >
                      {severity}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Mitigation Plan *</Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                placeholder="Enter mitigation plan"
                placeholderTextColor="#94A3B8"
                value={risk.mitigation}
                onChangeText={text => setRisk({ ...risk, mitigation: text })}
                multiline
                numberOfLines={3}
                textAlignVertical="top"
              />
            </View>
            <TouchableOpacity style={styles.modalActionButton} onPress={handleAddRisk}>
              <Text style={styles.modalActionButtonText}>Add Risk</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Add Tag Modal */}
      <Modal animationType="slide" transparent={true} visible={showTagModal} onRequestClose={() => setShowTagModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Add Tag</Text>
              <TouchableOpacity style={styles.modalCloseButton} onPress={() => setShowTagModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Tag *</Text>
              <TextInput
                style={styles.input}
                placeholder="Enter tag (e.g., Commercial, Residential)"
                placeholderTextColor="#94A3B8"
                value={tag}
                onChangeText={setTag}
              />
            </View>
            <TouchableOpacity style={styles.modalActionButton} onPress={handleAddTag}>
              <Text style={styles.modalActionButtonText}>Add Tag</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#4A90E2' },
  header: {
    backgroundColor: '#4A90E2',
    paddingVertical: 15,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: { padding: 5 },
  headerCenter: { flexDirection: 'row', alignItems: 'center' },
  headerLogo: { width: 30, height: 30, borderRadius: 15, resizeMode: 'cover' },
  headerTitle: { color: '#fff', fontSize: 20, fontWeight: 'bold', marginLeft: 10 },
  container: { padding: 16 },
  section: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 10,
    elevation: 2,
  },
  sectionTitle: { fontSize: 18, fontWeight: '600', color: '#0f172a', marginBottom: 16 },
  sectionHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  addButton: {
    backgroundColor: '#0e4e96',
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
  },
  addButtonText: { color: '#fff', fontSize: 14, fontWeight: '500', marginLeft: 4 },
  inputGroup: { marginBottom: 16 },
  inputLabel: { fontSize: 14, fontWeight: '500', color: '#334155', marginBottom: 8 },
  input: {
    backgroundColor: '#f1f5f9',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: '#0f172a',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  textArea: { minHeight: 100, paddingTop: 12 },
  rowInputs: { flexDirection: 'row', justifyContent: 'space-between' },
  dateInput: {
    backgroundColor: '#f1f5f9',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dateText: { fontSize: 16, color: '#0f172a' },
  optionsContainer: { flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: -4 },
  optionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginHorizontal: 4,
    marginBottom: 8,
    backgroundColor: '#f1f5f9',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  optionButtonActive: { backgroundColor: '#fff', borderWidth: 1 },
  optionButtonText: { fontSize: 14, color: '#64748B' },
  optionButtonTextActive: { fontWeight: '600' },
  statusDot: { width: 10, height: 10, borderRadius: 5, marginRight: 6 },
  priorityDot: { width: 10, height: 10, borderRadius: 5, marginRight: 6 },
  severityDot: { width: 10, height: 10, borderRadius: 5, marginRight: 6 },
  emptyState: { alignItems: 'center', justifyContent: 'center', padding: 20, backgroundColor: '#f8fafc', borderRadius: 8 },
  emptyStateText: { fontSize: 14, color: '#64748B', marginTop: 8 },
  teamList: { marginTop: 4 },
  teamMember: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    marginBottom: 8,
  },
  teamMemberInfo: { flex: 1 },
  teamMemberName: { fontSize: 16, fontWeight: '500', color: '#0f172a' },
  teamMemberRole: { fontSize: 14, color: '#64748B', marginTop: 2 },
  removeButton: { padding: 4 },
  milestonesList: { marginTop: 4 },
  milestoneItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    marginBottom: 8,
  },
  milestoneInfo: { flex: 1 },
  milestoneTitle: { fontSize: 16, fontWeight: '500', color: '#0f172a', marginBottom: 4 },
  milestoneDetails: { flexDirection: 'row', alignItems: 'center' },
  milestoneDetail: { flexDirection: 'row', alignItems: 'center', marginRight: 16 },
  milestoneDetailText: { fontSize: 14, color: '#64748B', marginLeft: 4 },
  risksList: { marginTop: 4 },
  riskItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    marginBottom: 8,
  },
  riskInfo: { flex: 1 },
  riskHeader: { marginBottom: 8 },
  severityBadge: { alignSelf: 'flex-start', paddingVertical: 4, paddingHorizontal: 8, borderRadius: 4 },
  severityText: { fontSize: 12, fontWeight: '500' },
  riskDescription: { fontSize: 14, color: '#0f172a', marginBottom: 8 },
  riskMitigation: { flexDirection: 'row', alignItems: 'flex-start' },
  riskMitigationLabel: { fontSize: 13, fontWeight: '500', color: '#64748B', marginRight: 4 },
  riskMitigationText: { fontSize: 13, color: '#334155', flex: 1 },
  tagsContainer: { flexDirection: 'row', flexWrap: 'wrap', marginTop: 4 },
  tag: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f1f5f9',
    borderRadius: 16,
    paddingVertical: 6,
    paddingHorizontal: 12,
    marginRight: 8,
    marginBottom: 8,
  },
  tagText: { fontSize: 14, color: '#0f172a', marginRight: 4 },
  removeTagButton: { padding: 2 },
  saveButton: {
    backgroundColor: '#0e4e96',
    borderRadius: 8,
    paddingVertical: 14,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    marginBottom: 20,
  },
  saveButtonText: { color: '#fff', fontSize: 16, fontWeight: '600', marginLeft: 8 },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' },
  modalContent: { backgroundColor: '#fff', borderRadius: 12, width: '90%', padding: 20, maxHeight: '80%' },
  modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  modalTitle: { fontSize: 18, fontWeight: '600', color: '#0f172a' },
  modalCloseButton: { padding: 4 },
  modalActionButton: { backgroundColor: '#0e4e96', borderRadius: 8, paddingVertical: 12, alignItems: 'center', justifyContent: 'center', marginTop: 8 },
  modalActionButtonText: { color: '#fff', fontSize: 16, fontWeight: '600' },
});